import { SubscriptionTag } from '@models/membership/SubscriptionTag'
import { subscriptionTagSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createSubscriptionTag as createSubscriptionTagAPI,
  deleteSubscriptionTag as deleteSubscriptionTagAPI,
  fetchSubscriptionTagById,
  fetchSubscriptionTags,
  updateSubscriptionTag as updateSubscriptionTagAPI,
} from '@services/membership/SubscriptionTagService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<SubscriptionTag> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'subscriptionTag',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSubscriptionTagsSuccess,
  setQuery: setSubscriptionTagQuery,
  reset: resetSubscriptionTagState,
  resetQueryAndIds: resetSubscriptionTagQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: SubscriptionTag }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSubscriptionTags = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSubscriptionTag = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSubscriptionTagById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSubscriptionTagsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedSubscriptionTag =
  (SubscriptionTag: SubscriptionTag | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<SubscriptionTag>(SubscriptionTag)
      )
    )

export const getSubscriptionTags = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSubscriptionTags(params),
    [subscriptionTagSchema],
    slice.actions
  )

export const createSubscriptionTag = (tag: SubscriptionTag) =>
  SBAPI.CreateDispatched(
    createSubscriptionTagAPI(tag),
    subscriptionTagSchema,
    slice.actions
  )

export const getSubscriptionTagWithId = (id: number) =>
  SBAPI.FetchDispatched<SubscriptionTag>(
    fetchSubscriptionTagById(id),
    subscriptionTagSchema,
    slice.actions
  )

export const updateSubscriptionTag = (SubscriptionTag: SubscriptionTag) =>
  SBAPI.UpdateDispatched<SubscriptionTag>(
    updateSubscriptionTagAPI(SubscriptionTag),
    SubscriptionTag,
    slice.actions
  )

export const deleteSubscriptionTag = (SubscriptionTag: SubscriptionTag) =>
  SBAPI.DestroyDispatched<SubscriptionTag>(
    deleteSubscriptionTagAPI(SubscriptionTag),
    SubscriptionTag,
    slice.actions
  )
